import { useState, useEffect, useRef, useMemo } from 'react';
import { t } from 'i18next';
import explorerImage from '../../../assets/explorer.webp';
import atlasImage from '../../../assets/atlas.png';

const ConfigPatient = ({ config, isAtlas }) => {
  const [imageSize, setImageSize] = useState({ width: 1920, height: 1080 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);

  const imgSrc = isAtlas ? atlasImage : explorerImage;
  const imgAlt = isAtlas ? 'Atlas' : 'Explorer';

  useEffect(() => {
    const handleResize = () => {
      if (imgRef.current) {
        const { width, height } = imgRef.current.getBoundingClientRect();
        setImageSize({ width, height });
      }
    };

    // Llama a handleResize también cuando la imagen se haya cargado
    if (imageLoaded) {
      handleResize();
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageLoaded]);

  const {
    femurPinInt: femurPinIntR,
    femurPinExt: femurPinExtR,
    tibiaPinInt: tibiaPinIntR,
    tibiaPinExt: tibiaPinExtR,
  } = computePins(config.femur_len_r, config.tibia_len_r);

  const {
    femurPinInt: femurPinIntL,
    femurPinExt: femurPinExtL,
    tibiaPinInt: tibiaPinIntL,
    tibiaPinExt: tibiaPinExtL,
  } = computePins(config.femur_len_l, config.tibia_len_l);

  const originalImageSize = { width: 1920, height: 1080 };

  const displayValue = (value) => value ?? 'N/A';

  const configItems = useMemo(
    () => [
      // Swapped right side values to the left
      {
        id: 1,
        label: (
          <>
            <strong>{t('pages.maintenance.ingestion.flexkneel')}</strong>:{' '}
            {displayValue(config.flexor_knee_flex_l)} °<br />
            <strong>{t('pages.maintenance.ingestion.femurl')}</strong>:{' '}
            {displayValue(config.femur_len_l)} cm
            {!isAtlas && (
              <>
                {' ('}
                <strong>int</strong>: {femurPinIntL} <strong>ext</strong>:{' '}
                {femurPinExtL}
                {') '}
              </>
            )}
          </>
        ),
        x: 3853,
        y: 650,
      },
      {
        id: 2,
        label: (
          <>
            <strong>{t('pages.maintenance.ingestion.flexoshipl')}</strong>:{' '}
            {displayValue(config.flexor_hip_flex_l)} °<br />
            <strong>{t('pages.maintenance.ingestion.abdl')}</strong>:{' '}
            {displayValue(config.hip_abd_l)} °
          </>
        ),
        x: 3793,
        y: 450,
      },
      {
        id: 3,
        label: (
          <>
            <strong>{t('pages.maintenance.ingestion.tibial')}</strong>:{' '}
            {displayValue(config.tibia_len_l)} cm
            {!isAtlas && (
              <>
                {' ('}
                <strong>int</strong>: {tibiaPinIntL} <strong>ext</strong>:{' '}
                {tibiaPinExtL}
                {') '}
              </>
            )}
          </>
        ),
        x: 3793,
        y: 850,
      },
      {
        id: 4,
        label: (
          <>
            <strong>{t('pages.patients.form.height')}</strong>:{' '}
            {displayValue(config.height)} cm <br />
            <strong>{t('pages.patients.form.weight')}</strong>:{' '}
            {displayValue(config.weight)} kg <br />
            <strong>{t('pages.maintenance.ingestion.chest')}</strong>:{' '}
            {displayValue(config.chest)} <br />
            {!isAtlas ? (
              <>
                <strong>{t('pages.maintenance.ingestion.corset')}</strong>:{' '}
                {displayValue(t(config.corset))} <br />
                <strong>{t('pages.maintenance.ingestion.walker')}</strong>:{' '}
                {displayValue(config.walker_len) ? t('yes') : t('no')}{' '}
              </>
            ) : (
              <>
                <strong>
                  {t('pages.maintenance.ingestion.heightmarcoatlas')}
                </strong>
                : {displayValue(config.height_marco)} cm <br />{' '}
              </>
            )}
          </>
        ),
        x: 2887,
        y: 25,
      },
      {
        id: 5,
        label: (
          <>
            <strong>{t('pages.maintenance.ingestion.flexkneer')}</strong>:{' '}
            {displayValue(config.flexor_knee_flex_r)} °<br />
            <strong>{t('pages.maintenance.ingestion.femurr')}</strong>:{' '}
            {displayValue(config.femur_len_r)} cm
            {!isAtlas && (
              <>
                {' ('}
                <strong>int</strong>: {femurPinIntR} <strong>ext</strong>:{' '}
                {femurPinExtR}
                {') '}
              </>
            )}
          </>
        ),
        x: 1403,
        y: 650,
      },
      {
        id: 6,
        label: (
          <>
            <strong>{t('pages.maintenance.ingestion.tibiar')}</strong>:{' '}
            {displayValue(config.tibia_len_r)} cm
            {!isAtlas && (
              <>
                {' ('}
                <strong>int</strong>: {tibiaPinIntR} <strong>ext</strong>:{' '}
                {tibiaPinExtR}
                {') '}
              </>
            )}
          </>
        ),
        x: 1403,
        y: 850,
      },
      {
        id: 7,
        label: (
          <>
            <strong>{t('pages.maintenance.ingestion.shoe')}</strong>:{' '}
            {displayValue(config.shoe)} <br />
            {!isAtlas ? (
              <>
                <strong>{t('pages.maintenance.ingestion.anckle_lock')}</strong>:{' '}
                {displayValue(config.anckle_lock) ? t('yes') : t('no')}
                <br />
              </>
            ) : (
              <>
                <strong>
                  {t('pages.maintenance.ingestion.plantar_flex_max')}
                </strong>
                : {displayValue(config.flex_plantar_max)} ° <br />
                <strong>
                  {t('pages.maintenance.ingestion.dorsal_flex_max')}
                </strong>
                : {displayValue(config.flex_dorsal_max)} ° <br />
              </>
            )}
          </>
        ),
        x: 1503,
        y: 990,
      },
      {
        id: 8,
        label: (
          <>
            <strong>{t('pages.maintenance.ingestion.flexoshipr')}</strong>:{' '}
            {displayValue(config.flexor_hip_flex_r)} °<br />
            <strong>{t('pages.maintenance.ingestion.abdr')}</strong>:{' '}
            {displayValue(config.hip_abd_r)} °
          </>
        ),
        x: 1803,
        y: 450,
      },
    ],
    [config, t, isAtlas]
  );

  const scaleX = (x) => (x / originalImageSize.width) * imageSize.width;
  const scaleY = (y) => (y / originalImageSize.height) * imageSize.height;

  useEffect(() => {
    const handleResize = () => {
      if (imgRef.current) {
        const { width, height } = imgRef.current.getBoundingClientRect();
        setImageSize({ width, height });
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <div className="relative flex justify-center">
      <img
        ref={imgRef}
        src={imgSrc}
        alt={imgAlt}
        loading="lazy"
        className="w-4 h-auto mx-auto pt-8"
        onLoad={handleImageLoad}
      />
      {imageLoaded &&
        configItems.map((item) => {
          // Validar coordenadas y ajustar top/left
          const topPosition = Math.max(0, scaleY(item.y) - 30);
          const leftPosition = Math.max(0, scaleX(item.x) - 60);

          return (
            <div
              key={item.id}
              className="absolute bg-gray-700 text-white text-sm px-2 py-1 rounded cursor-move transition-opacity duration-300"
              style={{
                top: `${topPosition}px`,
                left: `${leftPosition}px`,
                whiteSpace: 'pre-wrap',
                opacity: imageLoaded ? 1 : 0,
              }}
            >
              {item.label}
            </div>
          );
        })}
    </div>
  );
};

export default ConfigPatient;

// Helper function for pin computation
export const computePins = (femur: number, tibia: number) => {
  let femurPinInt: number | string = 'N/A';
  let femurPinExt: number | string = 'N/A';
  let tibiaPinInt: number | string = 'N/A';
  let tibiaPinExt: number | string = 'N/A';

  // Femur Pin Calculation
  if (femur >= 21 && femur <= 28) {
    femurPinInt = 1;
    femurPinExt = (femur - 21 + 1).toString();
  } else if (femur >= 29 && femur <= 32) {
    femurPinInt = 2;
    femurPinExt = (femur - 29 + 5).toString();
  } else if (femur >= 33 && femur <= 36) {
    femurPinInt = 3;
    femurPinExt = (femur - 29 + 1).toString();
  }

  // Tibia Pin Calculation
  if (tibia >= 20 && tibia <= 27) {
    tibiaPinInt = 1;
    tibiaPinExt = (tibia - 20 + 1).toString();
  } else if (tibia >= 28 && tibia <= 31) {
    tibiaPinInt = 2;
    tibiaPinExt = (tibia - 28 + 5).toString();
  } else if (tibia >= 32 && tibia <= 35) {
    tibiaPinInt = 3;
    tibiaPinExt = (tibia - 28 + 1).toString();
  }

  return { femurPinInt, femurPinExt, tibiaPinInt, tibiaPinExt };
};
